import { H3, H4, PaddedSurface, Paragraph } from "App/global-style";
import { AlertType, AlertWithIllustration } from "Components/Form/Alert";
import React, { PropsWithChildren, ReactElement } from "react";
import { BoldText, TransparentSurface, VehicleContainer } from "../styled";

import { useTexts } from "Components/TextContext";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { IllustrationName } from "Constants/constants";
import { isDefined } from "Utils/functions";
import { FormikProps } from "formik";
import { CarProductIntroFormValues } from "../interfaces";
import { ActionButton } from "./components/ActionButton";
import { MetaInformation } from "./components/MetaInformation";
import { useViewModel } from "./hooks";

interface Props {
  readonly formProps: FormikProps<CarProductIntroFormValues>;
}

export const OwnedVehicles = (
  props: PropsWithChildren<Props>,
): ReactElement | null => {
  const { formProps, children } = props;
  const { getText } = useTexts();

  const {
    data: { vehicles, error, isLoading, renderView },
    handlers: { selectVehicle },
  } = useViewModel(formProps);

  if (!renderView) {
    return null;
  }

  if (isLoading) {
    return <ThemedSpinner />;
  }

  const heading = getText("carform.label.owned");

  if (isDefined(error)) {
    return (
      <PaddedSurface>
        <H3>{heading}</H3>
        <AlertWithIllustration
          title={getText("carform.alerttitle.owned_http-error")}
          description={getText("carform.alertdescription.owned_http-error")}
          alertType={AlertType.SYSTEM_ERROR_ALERT}
          illustrationName={IllustrationName.SOMETHING_WENT_WRONG}
          alertId="fetchVehiclesError"
          flexDirection="row-reverse"
        />
      </PaddedSurface>
    );
  }

  if (vehicles.length === 0) {
    return (
      <PaddedSurface>
        <H3>{heading}</H3>
        <TransparentSurface role="alert" variant="border">
          <H4>{getText("carform.alerttitle.owned")}</H4>
          <Paragraph>{getText("carform.alertdescription.owned")}</Paragraph>
        </TransparentSurface>
      </PaddedSurface>
    );
  }

  return (
    <PaddedSurface>
      <H3>{heading}</H3>
      {vehicles.map((vehicle) => (
        <VehicleContainer
          key={`${vehicle.configurationId}-${vehicle.regNumber}`}
          variant="border"
        >
          <div>
            <BoldText>
              {vehicle.brand} {vehicle.model} {vehicle.registrationYear}
            </BoldText>
            <br />
            <span>
              {vehicle.regNumber} - <MetaInformation vehicle={vehicle} />
            </span>
          </div>
          <ActionButton vehicle={vehicle} selectVehicle={selectVehicle} />
        </VehicleContainer>
      ))}
      {children}
    </PaddedSurface>
  );
};
